"use client";

import React from "react";
import Image from "next/image";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

const carouselSlides = [
  {
    id: 1,
    image: "/assets/images/auth-and-onboarding-bg.webp",
    title: "Shop What You Need, Today",
    description: "Access the latest product with just credit from your salary",
  },
  {
    id: 2,
    image: "/assets/images/auth-and-onboarding-bg2.webp",
    title: "Pay Your Way",
    description: "Flexible payment options designed for your convenience",
  },
  {
    id: 3,
    image: "/assets/images/auth-and-onboarding-bg3.webp",
    title: "Designed for Civil Servants",
    description: "Built to fit your lifestyle and budget needs",
  },
];

export default function OnboardingandAuthlayout({ children }: { children: React.ReactNode }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const plugin = React.useRef(Autoplay({ delay: 3000, stopOnInteraction: false }));

  React.useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className='flex h-svh w-screen overflow-hidden'>
      {/* Left side - Carousel */}
      <div className='relative hidden h-full lg:flex lg:w-[45%]'>
        <Carousel
          setApi={setApi}
          className='h-full w-full [&>div]:h-full'
          opts={{
            align: "start",
            loop: true,
          }}
          plugins={[plugin.current]}
        >
          <CarouselContent className='ml-0 h-full [&>div]:h-full'>
            {carouselSlides.map((slide) => (
              <CarouselItem key={slide.id} className='h-full pl-0'>
                <div className='relative h-full w-full'>
                  <Image src={slide.image} alt={slide.title} fill className='object-cover' priority={true} />

                  {/* Overlay gradient */}
                  <div className='absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent' />

                  {/* Text Content */}
                  <div className='absolute right-0 bottom-25 left-0 p-8 text-white'>
                    <h2 className='mb-5 max-w-xs text-2xl font-semibold lg:text-3xl xl:text-4xl'>{slide.title}</h2>
                    <p className='text-sxm text-white/90 lg:text-base'>{slide.description}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Dots Navigation - Outside carousel */}
        <div className='absolute inset-x-0 bottom-6 z-[100] flex justify-center gap-2 px-8'>
          {carouselSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                current === index ? "bg-link-primary w-8" : "w-2 bg-white hover:bg-white/80"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Right side - Form content */}
      <div className='bg-bg-base flex w-full items-center justify-center p-8 lg:w-[55%]'>
        <div className='w-full max-w-md'>{children}</div>
      </div>
    </div>
  );
}
